"use client";

// SNOWCLUB-MOUNTAIN-PICKER-MAP-STAGING-20261025 — THE HEADER DROPDOWN'S MAP, ON THE HOMEPAGE SVG.
//
// What a customer actually opens: on /mountain-whistler "Switch mountain" → "SHOW THE MAP" never reaches
// MountainSelectModal. The design runtime answers `sg:mountain-map` itself (sg-beta-v2.01.html, the listener
// at ~7854) and shows its own pane, `section[data-sg-menu="mtn"] [data-mtn-map-pane]`, filled with 24 raster
// tiles and percentage-positioned pins; below 768 it hides both the pane and the "Show the map" toggle
// (`mtnMapDisplay` / `mtnMapToggleDisplay`, ~13802). The canonical HTML and its compiled runtime stay unchanged,
// so this layer hydrates that ONE pane in place, the way TransportMapPanelLayer hydrates the transport panel:
//
//   • the pane's own children (tile world, pins, zoom buttons, hint) are withdrawn by a scoped stylesheet keyed
//     on an attribute this layer sets — removing the attribute restores them exactly;
//   • the tested HomepageSvgMap is mounted into a host inside the pane, fed by the SAME catalogue, marker hook
//     and pin resolver the fallback selector uses, so a pin says and links what its row does;
//   • the existing list rows keep driving it: hovering a row lights its pin, the row's "Show on map" button
//     locates it, and the host's `sg:mountain-map` locate request is honoured;
//   • below 768 the existing toggle is shown and, while the runtime's own toggle reads "Hide map", the pane is
//     shown under the list with the stacked layout the fallback selector already uses (mountain-select.ts).
//
// The pane's legacy pointer/keyboard handlers and the document-level wheel zoom (registered once by the runtime,
// acting on anything inside [data-mtn-map-pane]) must never see the new map's gestures, so the host stops those
// events from bubbling past itself. d3-zoom's own listeners sit on the SVG (wheel, touch) and on the window for
// a mouse drag; mouse events are therefore left alone and only the pointer/wheel/key events the legacy pane
// listens for are contained.

import { useEffect, useMemo, useReducer, useState } from "react";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import HomepageSvgMap from "./HomepageSvgMap";
import { resolveHomepagePins } from "./homepage-map-view";
import { RESORTS } from "@/components/site/resorts";
import { mountainCodeFromPath } from "@/components/site/hub-nav";
import { bookabilityOf, useMountainCatalogue } from "@/components/site/mountain-catalogue";
import { useSelectorMarkers } from "@/components/site/selector-markers";
import { destinationFor, readSwitchIntent } from "@/components/site/switch-intent";
import { selectLayout } from "@/lib/mountain-select";

/** The runtime's own markup (sg-beta-v2.01.html ~4788–4826). Header buttons share data-sg-menu="mtn"; only the
 *  panel is a <section>. */
export const SELECTOR_SECTION = 'section[data-sg-menu="mtn"]';
export const SELECTOR_PANE = '[data-kit-tile-map="export"][data-mtn-map-pane]';
export const SELECTOR_TOGGLE = "button[data-mtn-open-map]";
export const SELECTOR_ROW_PIN = 'button[aria-label="Show on map"]';
/** set on the section while the bridge is attached; every style below is keyed on it */
export const BRIDGE_ATTR = "data-sg-home-map";
/** mirrors the runtime's own toggle state (its label), so CSS can show the pane on a phone */
export const WANTED_ATTR = "data-sg-home-map-wanted";
export const HOST_ATTR = "data-sg-home-map-root";

/** The runtime's toggle reads "Hide map" exactly while its `mtnMap` state is on (`mtnMapToggleLabel`). */
export const mapWantedFromToggle = (label: string | null | undefined): boolean => /\bhide map\b/i.test(label ?? "");

const norm = (s: string) => s.replace(/,\s*[A-Z]{2}$/, "").trim().toLowerCase();
/** A list row's visible name → its mountain code, through the same RESORTS roster the selector lists. */
export function rowCode(name: string | null | undefined): string | null {
  const n = norm(name ?? "");
  if (!n) return null;
  const hit = RESORTS.find((r) => norm(r.label) === n);
  return hit ? mountainCodeFromPath(hit.href) : null;
}

const ROSTER_CODES = new Set(RESORTS.map((r) => mountainCodeFromPath(r.href)));
/** A row link's href → its mountain code, only when the RESORTS roster lists that code; anything else is a miss. */
export function rowCodeFromHref(href: string | null | undefined): string | null {
  if (!href) return null;
  let pathname: string;
  try {
    pathname = new URL(href, "https://row.invalid").pathname;
  } catch {
    return null;
  }
  const code = mountainCodeFromPath(pathname);
  return code && ROSTER_CODES.has(code) ? code : null;
}

/** The structural slice of an element the row resolver reads (a real Element satisfies it). */
export interface SelectorRowNode {
  closest(selector: string): SelectorRowNode | null;
  querySelector(selector: string): { getAttribute(name: string): string | null } | null;
}
/**
 * A list row is the runtime's `div` whose direct children are the mountain link (`a[href]`) and the separate
 * "Show on map" button. The row's identity is that link's href, read from the outer row for any target inside
 * it — the link's nested spans or the pin's nested svg path alike. `pin` is whether the target is the pin.
 */
export function selectorRowOf(t: unknown): { code: string | null; pin: boolean } | null {
  const el = t && typeof (t as SelectorRowNode).closest === "function" ? (t as SelectorRowNode) : null;
  const row = el?.closest("div");
  if (!el || !row?.querySelector(`:scope > ${SELECTOR_ROW_PIN}`)) return null;
  return { code: rowCodeFromHref(row.querySelector(":scope > a[href]")?.getAttribute("href")), pin: Boolean(el.closest(SELECTOR_ROW_PIN)) };
}

const cssDecl = (o: Record<string, unknown>, keys: string[]) =>
  keys.filter((k) => o[k] !== undefined).map((k) => `${k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}:${String(o[k])}${typeof o[k] === "number" ? "px" : ""} !important`).join(";");

/** The scoped stylesheet. Desktop display stays the runtime's; below 768 the stacked layout is the fallback
 *  selector's own (`selectLayout(true, true)`), applied only while the runtime's toggle is on. */
export function selectorMapCss(): string {
  const stacked = selectLayout(true, true);
  const S = `${SELECTOR_SECTION}[${BRIDGE_ATTR}="on"]`;
  return [
    `${S} ${SELECTOR_PANE} > :not([${HOST_ATTR}]){display:none !important}`,
    `@media (max-width: 767px){`,
    `${S} ${SELECTOR_TOGGLE}{display:flex !important}`,
    `${S}[${WANTED_ATTR}="true"] ${SELECTOR_PANE}{display:block !important;${cssDecl(stacked.map as Record<string, unknown>, ["flex", "minHeight"])}}`,
    `${S}[${WANTED_ATTR}="true"] > div:first-child{${cssDecl(stacked.list as Record<string, unknown>, ["flex", "maxHeight", "borderBottom"])}}`,
    `}`,
  ].join("\n");
}

/** The events the legacy pane (and the runtime's document wheel zoom) listen for. Escape still bubbles so the
 *  panel closes as before; full screen consumes its own Escape at window capture first. */
export const CONTAINED_EVENTS = ["pointerdown", "pointermove", "pointerup", "pointercancel", "wheel", "keydown"] as const;
const contain = (e: Event) => {
  if (e.type === "keydown" && (e as KeyboardEvent).key === "Escape") return;
  e.stopPropagation();
};

interface BridgeEl {
  querySelector(sel: string): BridgeEl | null;
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
  appendChild(child: BridgeEl): unknown;
  addEventListener(type: string, fn: (e: Event) => void, opts?: boolean | AddEventListenerOptions): void;
  removeEventListener(type: string, fn: (e: Event) => void, opts?: boolean | EventListenerOptions): void;
  remove(): void;
  textContent: string | null;
  style: { cssText: string };
}
export interface BridgeEnv {
  createElement(tag: string): BridgeEl;
  head: { appendChild(child: BridgeEl): unknown };
  /** watch a node's text (the toggle's label); returns the disconnect */
  watch(target: BridgeEl, onChange: () => void): () => void;
  /** mount the map into the host; returns the unmount */
  mount(host: BridgeEl, section: BridgeEl, inbox?: LocateLease): () => void;
}

/** One attached section: the host it owns and the full, reversible cleanup. */
export interface Attached { section: BridgeEl; host: BridgeEl; cleanup: () => void }

/**
 * Attach the homepage map to ONE live selector section. Returns what it attached, or null when the section is
 * not the selector panel (no pane / no toggle): then nothing is touched.
 */
export function attachSelectorMap(section: BridgeEl, env: BridgeEnv): Attached | null {
  const pane = section.querySelector(SELECTOR_PANE);
  const toggle = section.querySelector(SELECTOR_TOGGLE);
  if (!pane || !toggle) return null;

  const style = env.createElement("style");
  style.setAttribute("data-sg-home-map-style", "");
  style.textContent = selectorMapCss();
  env.head.appendChild(style);

  const host = env.createElement("div");
  host.setAttribute(HOST_ATTR, "");
  host.style.cssText = "position:absolute;inset:0;z-index:1;";
  for (const t of CONTAINED_EVENTS) host.addEventListener(t, contain);
  pane.appendChild(host);

  const sync = () => section.setAttribute(WANTED_ATTR, mapWantedFromToggle(toggle.textContent) ? "true" : "false");
  sync();
  section.setAttribute(BRIDGE_ATTR, "on");
  const unwatch = env.watch(toggle, sync);
  const unmount = env.mount(host, section);

  const cleanup = () => {
    unwatch();
    unmount();
    for (const t of CONTAINED_EVENTS) host.removeEventListener(t, contain);
    host.remove();
    style.remove();
    section.removeAttribute(BRIDGE_ATTR);
    section.removeAttribute(WANTED_ATTR);
  };
  return { section, host, cleanup };
}

/**
 * The host's explicit locate (`sg:mountain-map` detail.locate), held for the layer's whole lifetime. On the closed
 * home page the runtime CREATES the panel while answering that event, so the event is over before any pane exists
 * — let alone the pane's React consumer, which connects one observer delivery plus one React commit later. A
 * request with no consumer is held (the latest one wins) and handed over exactly once when a consumer connects; a
 * connected consumer gets it at once and nothing is held.
 */
/**
 * One attachment's right to receive. Only the newest lease may hold the consumer: a pane being retired is revoked
 * SYNCHRONOUSLY by the tracker, while its React root unmounts later (deferred), so its late effect cleanup — or a
 * late first connect from a root that committed after retirement — must not touch the next pane or the held request.
 */
export interface LocateLease {
  /** returns the disconnect; a held request is delivered to `fn` immediately and dropped. A revoked or superseded
   *  lease connects to nothing and takes nothing. */
  connect(fn: (code: string) => void): () => void;
  revoke(): void;
}
export interface LocateInbox {
  post(code: string): void;
  /** a new lease for a newly attached pane; it supersedes (and so revokes) any earlier one */
  lease(): LocateLease;
  clear(): void;
}
export function locateInbox(): LocateInbox {
  let held: string | null = null;
  let active: object | null = null;
  let owner: { token: object; fn: (code: string) => void } | null = null;
  return {
    post(code) {
      // `owner` only ever belongs to the active lease: lease() and revoke() clear it synchronously
      if (owner) owner.fn(code);
      else held = code;
    },
    lease() {
      const token = {};
      active = token;
      if (owner && owner.token !== token) owner = null; // the previous pane stops receiving now, not at its unmount
      return {
        connect(fn) {
          if (active !== token) return () => undefined;
          const mine = { token, fn };
          owner = mine;
          const code = held;
          held = null;
          if (code) fn(code);
          return () => { if (owner === mine) owner = null; };
        },
        revoke() {
          if (active === token) active = null;
          if (owner?.token === token) owner = null;
        },
      };
    },
    clear() { held = null; },
  };
}

/** The host's `sg:mountain-map` request (MOUNTAIN_MAP_EVENT); only a non-empty string `locate` names a row. */
export function listenForMapRequests(target: EventTarget, onLocate: (code: string) => void): () => void {
  const on = (e: Event) => {
    const code = (e as CustomEvent<{ locate?: unknown } | null>).detail?.locate;
    if (typeof code === "string" && code) onLocate(code);
  };
  target.addEventListener("sg:mountain-map", on);
  return () => target.removeEventListener("sg:mountain-map", on);
}

export interface TrackEnv extends BridgeEnv {
  /** listen for the host's map request for as long as the layer is mounted; returns the unsubscribe */
  listen(onLocate: (code: string) => void): () => void;
  /** the live selector section, if the document has one now */
  find(): BridgeEl | null;
  /** still in the document */
  connected(el: BridgeEl): boolean;
  /** notify on document mutations for as long as the layer is mounted; returns the disconnect */
  observe(onChange: () => void): () => void;
}

/**
 * Keep exactly one bridge on whichever selector section the document holds, for the layer's whole lifetime.
 * The home page renders the panel only while it is open (`renderMountainPanel: s.page !== "home" || s.mtnOpen`),
 * so a section is created, removed and created again on every open; the runtime may also replace the pane.
 * Each document mutation re-checks: the same section with its host still in place is left alone (so this
 * layer's own host/attribute writes settle immediately — no feedback loop); a detached section or a stale host
 * is cleaned up (its React root unmounted) and the current section, if any, is attached. No timers.
 */
export function trackSelectorMap(env: TrackEnv): () => void {
  let cur: Attached | null = null;
  const inbox = locateInbox();
  // registered before anything is attached, so a request that creates the panel is never missed
  const unlisten = env.listen((code) => inbox.post(code));
  // each attachment gets its own lease, revoked synchronously when the tracker retires that attachment
  const bridgeEnv: BridgeEnv = {
    ...env,
    mount: (host, section) => {
      const lease = inbox.lease();
      const unmount = env.mount(host, section, lease);
      return () => { lease.revoke(); unmount(); };
    },
  };
  const sync = () => {
    const live = env.find();
    if (live && live === cur?.section && env.connected(live) && env.connected(cur.host)) return;
    if (cur) {
      // the panel this request opened is gone before its map took it (closed again): it is no longer wanted.
      // A stale host on the SAME section is only a re-attach, and keeps it.
      if (live !== cur.section) inbox.clear();
      cur.cleanup();
      cur = null;
    }
    if (live && env.connected(live)) cur = attachSelectorMap(live, bridgeEnv);
  };
  sync();
  const stop = env.observe(sync);
  return () => {
    stop();
    unlisten();
    inbox.clear();
    if (cur) { cur.cleanup(); cur = null; }
  };
}

/** The panel is open while the runtime lets it take pointer events (`mtnEvents: s.mtnOpen ? "auto" : "none"`). */
export const panelOpen = (style: { pointerEvents?: string } | null | undefined): boolean => (style?.pointerEvents ?? "") !== "none";

/** What the pane's map shows across the panel's open/close cycles and the two kinds of locate. */
export interface PaneState {
  open: boolean;
  focus: { key: string; nonce: number } | null;
  /** bumped on a NEUTRAL reopen: the map returns to the fit of every pin, as the design's toggleMountain resets */
  reset: number;
  /** a locate asked for since the panel last closed (sg:mountain-map opens the panel AND names a row) */
  locateSinceClose: boolean;
  nonce: number;
}
export type PaneEvent = { type: "open" } | { type: "close" } | { type: "locate"; code: string };
export const initialPane = (open: boolean): PaneState => ({ open, focus: null, reset: 0, locateSinceClose: false, nonce: 0 });
export function paneReducer(s: PaneState, e: PaneEvent): PaneState {
  if (e.type === "locate") {
    const nonce = s.nonce + 1;
    return { ...s, nonce, focus: { key: `m:${e.code}`, nonce }, locateSinceClose: true };
  }
  if (e.type === "close") return s.open ? { ...s, open: false, locateSinceClose: false } : s;
  if (s.open) return s; // not a transition
  // the runtime's sg:mountain-map handler dispatches synchronously, before its render opens the panel, so its
  // locate usually lands first; that opening is not neutral and keeps the locate. Any other opening resets.
  if (s.locateSinceClose) return { ...s, open: true };
  return { ...s, open: true, focus: null, reset: s.reset + 1 };
}

/** The map the host renders: the fallback selector's pins, driven by the runtime's own list rows. */
function SelectorPaneMap({ section, inbox }: { section: Element; inbox?: LocateLease }) {
  const [wanted, setWanted] = useState(() => section.getAttribute(WANTED_ATTR) === "true");
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [pane, dispatch] = useReducer(paneReducer, section instanceof HTMLElement ? panelOpen(section.style) : true, initialPane);

  useEffect(() => {
    // the bridge keeps WANTED_ATTR; the runtime keeps the panel's inline style (pointer-events while open). Only
    // those two attributes are watched, and this observer writes neither, so it cannot feed itself.
    const mo = new MutationObserver(() => {
      setWanted(section.getAttribute(WANTED_ATTR) === "true");
      const open = section instanceof HTMLElement ? panelOpen(section.style) : true;
      dispatch({ type: open ? "open" : "close" });
      if (!open) setHoverKey(null);
    });
    mo.observe(section, { attributes: true, attributeFilter: [WANTED_ATTR, "style"] });
    const rowOf = selectorRowOf;
    const onOver = (e: Event) => {
      // the map's own pins report their hover through `onHover`; only the list speaks here
      if (e.target instanceof Element && e.target.closest(`[${HOST_ATTR}]`)) return;
      const r = rowOf(e.target);
      setHoverKey(r?.code ? `m:${r.code}` : null);
    };
    const onLeave = () => setHoverKey(null);
    const onClick = (e: Event) => {
      const r = rowOf(e.target);
      if (r?.pin && r.code) dispatch({ type: "locate", code: r.code });
    };
    section.addEventListener("mouseover", onOver);
    section.addEventListener("mouseleave", onLeave);
    section.addEventListener("click", onClick, true);
    // the host's locate comes through the layer's persistent inbox (held if it arrived before this pane existed)
    const disconnect = inbox?.connect((code) => dispatch({ type: "locate", code }));
    return () => {
      mo.disconnect();
      section.removeEventListener("mouseover", onOver);
      section.removeEventListener("mouseleave", onLeave);
      section.removeEventListener("click", onClick, true);
      disconnect?.();
    };
  }, [section, inbox]);

  const { bookable, meta, listingCounts } = useMountainCatalogue(wanted);
  const switchIntent = useMemo(() => readSwitchIntent(null), []);
  const { markers } = useSelectorMarkers({ active: wanted, meta, listingCounts, bookable, switchIntent });
  const pins = useMemo(() => resolveHomepagePins(markers, RESORTS, mountainCodeFromPath, (code, href) => {
    const s = bookabilityOf(code, bookable);
    return { status: s === "unknown" ? undefined : s, href: destinationFor(href, switchIntent) };
  }), [markers, bookable, switchIntent]);

  if (!wanted) return null;
  return <HomepageSvgMap pins={pins} highlightKey={hoverKey} focus={pane.focus} onHover={setHoverKey} menuOwner="mtn" resetNonce={pane.reset} />;
}

export default function HomepageSelectorMapLayer() {
  useEffect(() => {
    const env: BridgeEnv = {
      createElement: (tag) => document.createElement(tag) as unknown as BridgeEl,
      head: document.head as unknown as BridgeEnv["head"],
      watch: (target, onChange) => {
        const mo = new MutationObserver(onChange);
        mo.observe(target as unknown as Node, { childList: true, characterData: true, subtree: true });
        return () => mo.disconnect();
      },
      mount: (host, section, inbox) => {
        const root: Root = createRoot(host as unknown as HTMLElement);
        root.render(<SelectorPaneMap section={section as unknown as Element} inbox={inbox} />);
        // deferred like the transport layer: unmounting synchronously inside a parent effect cleanup warns
        return () => { setTimeout(() => root.unmount(), 0); };
      },
    };
    const tracker: TrackEnv = {
      ...env,
      find: () => document.querySelector(SELECTOR_SECTION) as unknown as BridgeEl | null,
      connected: (el) => (el as unknown as Node).isConnected,
      listen: (onLocate) => listenForMapRequests(document, onLocate),
      observe: (onChange) => {
        const mo = new MutationObserver(onChange);
        mo.observe(document.body, { childList: true, subtree: true });
        return () => mo.disconnect();
      },
    };
    return trackSelectorMap(tracker);
  }, []);
  return null;
}
